import { Injectable, Renderer2, RendererFactory2 } from '@angular/core';
// import * as jsPDF from 'jspdf';
import { Observable } from 'rxjs/Observable';
import { config } from '../../config/config';
import { ButtonTriggerEvent } from '../common/components';
import { isNullOrUndefined } from '../common/extensions';
import { HubEvent } from '../models/hub/hub.event';
import { HubPlace } from '../models/hub/hub.place';
import { hexToRgb } from '../utils/color';
import { removeHtmlTags } from '../utils/removeHtmlTags';
import { EventTimeService } from './event.time.service';
import { ImageService } from './image.service';
import { upperFirstLetter } from '../../../server/src/common/extensions/array/string.extensions';
import { VisitorApiService } from './visitor.api.service';
import { ScriptService } from './script.service';

@Injectable()
export class ExportService {
  csvSeparator = ',';
  public pdfImageBase64: Array<ImgDataObject>;
  public doc: any;
  public twoLinesTitle: boolean;
  public type: PDFType;
  public itemCount = 0;
  public errorName = [];
  public loadImageTimeout = 1000 * 15;
  public isAllLoaded = false;
  public event: ButtonTriggerEvent;
  public logo: string;
  public mainColor: string;
  public isPrintVersion = false;
  public maxTimeBoxCount = 9;
  public pdfDateRange: string;
  public eventCalendarURL: string;
  public placeCalendarURL: string;
  public moreToursURL: string;
  public yPostion: number;
  public pdfDateFilterRange: string;
  public logoSize = {
    width: 68,
    height: 35,
  };
  public progressCallback: (progress) => void;
  public completeCallback: () => void;

  private renderer: Renderer2;

  constructor(
    private imageService: ImageService,
    protected apiService: VisitorApiService,
    private rendererFactory: RendererFactory2
  ) {
    this.renderer = this.rendererFactory.createRenderer(null, null);
    const SCRIPT_PATH = `https://unpkg.com/jspdf@latest/dist/jspdf.umd.min.js`;
    if (!ScriptService.isScriptExist(SCRIPT_PATH)) {
      const scriptElement = ScriptService.loadJsScript(this.renderer, SCRIPT_PATH);
      scriptElement.onload = () => {
        console.log('jsPDF loaded');
      };
    } else {
      console.log('jsPDF exist');
    }
  }

  exportCSV(value, columns, exportFilename) {
    const data = value;
    let csv = '\ufeff';
    for (let i = 0; i < columns.length; i++) {
      const column = columns[i];
      csv += '"' + (column.header || column) + '"';
      if (i < columns.length - 1) {
        csv += this.csvSeparator;
      }
    }
    data.forEach((record) => {
      csv += '\n';
      for (let i = 0; i < columns.length; i++) {
        const column = columns[i];
        let value = this.resolveFieldData(record, column) || '--';
        if (column == 'Description') {
          value = value.replace(',', '��').replace(/"/g, '""');
          let div = document.createElement('div');
          div.innerHTML = value;
          value = div.innerText;
        }
        csv += '"' + value + '"';
        if (i < columns.length - 1) {
          csv += this.csvSeparator;
        }
      }
    });
    const blob = new Blob([csv], {
      type: 'text/csv;charset=utf-8;',
    });
    if (window.navigator.msSaveOrOpenBlob) {
      navigator.msSaveOrOpenBlob(blob, exportFilename + '.csv');
    } else {
      const link = document.createElement('a');
      link.style.display = 'none';
      document.body.appendChild(link);
      if (link.download !== undefined) {
        link.setAttribute('href', URL.createObjectURL(blob));
        link.setAttribute('download', exportFilename + '.csv');
        link.click();
      } else {
        csv = 'data:text/csv;charset=utf-8,' + csv;
        window.open(encodeURI(csv));
      }
      document.body.removeChild(link);
    }
  }

  resolveFieldData(data, field) {
    if (data && field) {
      if (field.indexOf('.') === -1) {
        return data[field];
      } else {
        const fields = field.split('.');
        let value = data;
        for (let i = 0, len = fields.length; i < len; ++i) {
          if (value === null) {
            return null;
          }
          value = value[fields[i]];
        }
        return value;
      }
    } else {
      return null;
    }
  }

  public exportPDF(
    events: any,
    type: PDFType,
    isPrintVersion: boolean,
    topImage?: string,
    logo?: string,
    eventCalendarURL?: string,
    placeCalendarURL?: string,
    moreToursURL?: string,
    from?,
    to?,
    subTitle?: string,
    mainColor?: string,
    event?: ButtonTriggerEvent,
    progressCallback?: (progress) => void,
    completeCallback?: () => void
  ) {
    this.isAllLoaded = false;
    const { jsPDF } = window['jspdf'];
    this.doc = new jsPDF('p', 'mm', 'a4');
    //this.isEvent = isEvent;
    this.type = type;
    this.event = event;
    this.pdfImageBase64 = [];
    this.eventCalendarURL = eventCalendarURL;
    this.placeCalendarURL = placeCalendarURL;
    this.moreToursURL = moreToursURL;
    let items = events || []; // events.slice(0, 4);
    this.itemCount = items.filter((x) => !x.isTripTitle).length;
    let bgImage = '';
    if (topImage) {
      bgImage = topImage;
    } else {
      if (type === PDFType.event) {
        bgImage = this.imageService.imageSourceUrl(config.PDF.PDFTopEventBackgroundImage);
      } else if (type === PDFType.place) {
        bgImage = this.imageService.imageSourceUrl(config.PDF.PDFTopPlaceBackgroundImage);
      } else if (type === PDFType.tour) {
        bgImage = this.imageService.imageSourceUrl(config.PDF.PDFTopTripBackgroundImage);
      } else if (type === PDFType.trip) {
        bgImage = this.imageService.imageSourceUrl(config.PDF.PDFTopTripBackgroundImage);
      }
    }
    if (from && !to) {
      this.pdfDateRange = `AFTER ` + EventTimeService.toShortDateFormat(from);
      this.pdfDateFilterRange = `?start=` + EventTimeService.toDateFormat(from);
    } else if (!from && to) {
      this.pdfDateRange = `BEFORE ` + EventTimeService.toShortDateFormat(to);
      this.pdfDateFilterRange = `?end=` + EventTimeService.toDateFormat(to);
    } else if (from && to) {
      this.pdfDateRange = `${EventTimeService.toShortDateFormat(from)} - ${EventTimeService.toShortDateFormat(to)}`;
      this.pdfDateFilterRange = `?start=${EventTimeService.toDateFormat(from)}&end=${EventTimeService.toDateFormat(
        to
      )}`;
    } else if (subTitle) {
      this.pdfDateRange = subTitle;
      this.pdfDateFilterRange = '';
    } else {
      this.pdfDateRange = null;
      this.pdfDateFilterRange = '';
    }
    if (mainColor) {
      this.mainColor = mainColor;
    } else {
      this.mainColor = null;
    }
    this.isPrintVersion = isPrintVersion;
    this.progressCallback = progressCallback;
    this.completeCallback = completeCallback;
    this.pdfCreateImages(bgImage, 0, 'topBG');
    if (logo) {
      // if (logo.startsWith('assets/imgs')) {
      //     logo = window.location.href.substr(0, window.location.href.indexOf('hub/')) + logo;
      // }
      this.logo = logo;
    } else {
      this.logo = null;
    }

    for (let x = 0; x < items.length; x++) {
      if (items[x].isTripTitle) {
        this.pdfImageBase64.push(new ImgDataObject(true, items[x].tripTitle, x + 1, 0, 0, null, null, null));
      } else {
        if (items[x].description) {
          items[x].description = removeHtmlTags(items[x].description);
        }
        let imageUrl = this.imageService.imageSource(items[x]);
        let title = x + 1 + '';
        this.pdfCreateImages(imageUrl, x + 1, title, items[x]);
      }
    }
  }

  private savePrintPDF() {
    this.yPostion = 0;

    let header = this.pdfImageBase64.first((x) => x.title === 'topBG');
    if (!header) {
      setTimeout(() => {
        this.savePrintPDF();
      }, 1000);
      return;
    }

    this.pdfCreateBannerAndTitle().subscribe(() => {
      let imageHeight = 30,
        imageWidth = 46;
      let images = this.pdfImageBase64
        .filter((x) => x.title !== 'topBG' && x.title !== 'logo' && x.title !== 'logoBG')
        .sortBy((x) => x.index);
      images.forEach((x, index) => {
        this.yPostion = index === 0 ? 87 : this.yPostion + 15;
        if (this.yPostion > 251) {
          this.pdfNewPage();
        }
        if (x.isTripTitle) {
          this.doc.setFontSize(24);
          this.doc.setTextColor(0, 0, 0);
          x.title = x.title || '(no title)';
          x.title = this.replaceSpecialChar(x.title);
          let lines = this.doc.splitTextToSize(x.title, 140);
          lines.forEach((line, ind) => {
            if (ind < 4) {
              this.yPostion += ind === 0 ? 0 : 10;
              if (this.yPostion > 280) {
                this.pdfNewPage();
                this.yPostion = 15;
              }
              this.doc.text(
                64,
                this.yPostion,
                this.getAllowedText(line) + (ind === 3 && lines.length > 4 ? '...' : '')
              );
            }
          });
        } else {
          try {
            this.doc.addImage(x.base64, 'JPEG', 12, this.yPostion, imageWidth, imageHeight);
          } catch (e) {
            this.doc.addImage(this.logo, 'PNG', 12, this.yPostion, imageWidth, imageHeight);
          }
          this.pdfCreateTriangleAtLeftTop(this.yPostion);
          this.pdfCreateEventTitle(x.item, index, this.yPostion + 2);
          this.pdfCreateEventLocation(x.item, index, this.yPostion + 8);
          let descriptionYIncreasement = 0;
          if (x.item.startTime) {
            this.pdfCreateEventTime(x.item, index, this.yPostion + 2);
            this.pdfCreateEventTimeBox(x.item, index, this.yPostion + 1);
            descriptionYIncreasement = 10.5;
          } else {
            this.pdfCreatePlaceCategory(x.item as HubPlace, this.yPostion + 2);
            descriptionYIncreasement = 2;
            if (this.isPrintVersion) {
              this.pdfCreatePlaceInfoForPrint(x.item as HubPlace, this.yPostion + 2);
            }
          }

          this.pdfCreateDescriptionForPrint(x.item, this.yPostion + descriptionYIncreasement, index);
        }
      });

      this.doc.save(
        `${
          this.type === PDFType.event
            ? 'Events'
            : this.type === PDFType.place
            ? 'Places'
            : this.type === PDFType.tour
            ? ' Itineraries'
            : 'Trips'
        }.pdf`
      );
      this.event && this.event.complete && this.event.complete();
      if (this.completeCallback) {
        this.completeCallback();
      }
    });
  }

  private saveOnlinePDF() {
    let header = this.pdfImageBase64.first((x) => x.title === 'topBG');
    if (!header) {
      setTimeout(() => {
        this.saveOnlinePDF();
      }, 1000);
      return;
    }

    this.pdfCreateBannerAndTitle().subscribe(() => {
      let images = this.pdfImageBase64
        .filter((x) => x.title !== 'topBG' && x.title !== 'logo' && x.title !== 'logoBG')
        .sortBy((x) => x.index);

      images.forEach((x, index) => {
        let initIndex = index;
        let isFirstPage = index < 4;
        let isTripTitleTheFirstOfThePage = false;
        let thisPageTitleLineCount = 0;
        if ((index - 4) % 5 === 0) {
          this.doc.addPage();
          this.pdfCreateBGGrayColumn();
          this.pdfCreateBottomBanner();

          isTripTitleTheFirstOfThePage = !isFirstPage && x.isTripTitle;
          if (x.isTripTitle) {
            thisPageTitleLineCount = this.doc.splitTextToSize(x.title, 140).length;
          } else {
            thisPageTitleLineCount = 0;
          }
        } else {
          isTripTitleTheFirstOfThePage = !isFirstPage && images[index - ((index - 4) % 5)].isTripTitle;
        }

        let defaultHeight = 30,
          defaultWidth = 46;
        let height = defaultHeight;
        index = isFirstPage ? index : (index - 4) % 5;
        let y = 52 * index + 20;
        let newIndex = index;

        if (isFirstPage) {
          y += 67;
          newIndex++;
          if (images[0].isTripTitle && initIndex !== 0) {
            y -= 35;
          }
        }

        if (config.PDF.restrictImageRatio) {
          height = defaultWidth / x.widthHeightRatio;
          y = y - (height - defaultHeight) / 2;
        }

        if (x.isTripTitle) {
          this.doc.setFontSize(24);
          this.doc.setTextColor(0, 0, 0);
          x.title = x.title || '(no title)';
          x.title = this.replaceSpecialChar(x.title);
          let lines = this.doc.splitTextToSize(x.title, 140);
          let length = Math.min(lines.length, 4);
          let tripTitleY = y;
          if (initIndex !== 0 && index !== 0) {
            tripTitleY = (4 - length) * 7 + 15 + y;
          }
          lines.forEach((line, ind) => {
            if (ind < 4) {
              this.doc.text(
                64,
                tripTitleY + ind * 10,
                this.getAllowedText(line) + (ind === 3 && lines.length > 4 ? '...' : '')
              );
            }
          });
        } else {
          if (images[0].isTripTitle && isFirstPage) {
            this.doc.setFontSize(24);
            let lines = this.doc.splitTextToSize(images[0].title, 140);
            y += lines.length * 10;
          }
          if (isTripTitleTheFirstOfThePage) {
            this.doc.setFontSize(24);
            thisPageTitleLineCount = this.doc.splitTextToSize(
              images[initIndex - ((initIndex - 4) % 5)].title,
              140
            ).length;
            let length = Math.min(thisPageTitleLineCount, 4);
            y = y - (4 - length) * 7;
          }
          try {
            this.doc.addImage(x.base64, 'JPEG', 12, y, defaultWidth, height);
          } catch (e) {
            this.doc.addImage(this.logo, 'PNG', 12, y, defaultWidth, height);
          }
          this.pdfCreateEventTitle(x.item, newIndex, y + 2);
          this.pdfCreateEventLocation(x.item, newIndex, y + 8);
          if (x.item.startTime) {
            this.pdfCreateEventTime(x.item, newIndex, y + 15);
            this.pdfCreateEventTimeBox(x.item, newIndex, y + 15);
          } else {
            this.pdfCreatePlaceCategory(x.item as HubPlace, y + 13);
          }
          this.pdfCreateTriangleAtLeftTop(y);
          this.pdfCreateEventDescription(x.item, y + 26, initIndex);
        }
      });
      this.doc.save(
        `${
          this.type === PDFType.event
            ? 'Events'
            : this.type === PDFType.place
            ? 'Places'
            : this.type === PDFType.tour
            ? ' Itineraries'
            : 'Trips'
        }.pdf`
      );
      this.event && this.event.complete && this.event.complete();
      if (this.completeCallback) {
        this.completeCallback();
      }
    });
  }

  private pdfCreateBannerAndTitle(): Observable<any> {
    let header = this.pdfImageBase64.first((x) => x.title === 'topBG');

    // uploaded itinerary header image, crop it to the appropriate size (210mm * 60mm)
    if (header.height !== 0 && header.width !== 0) {
      const height = (210 * header.height) / header.width;
      const offsetY = (height - 60) / 2;
      console.log(`header: ${header.width}, ${header.height}, height: ${height}, offsetY: ${offsetY}`);

      this.doc.addImage(header.base64, 'JPEG', 0, -offsetY, 210, height);
      this.doc.addImage(config.PDF.PDFLogoBackgroundWhiteImage, 'JPEG', 0, 60, 210, offsetY);
    } else {
      this.doc.addImage(header.base64, 'JPEG', 0, 0, 210, 60);
    }

    // the gray column background behind images
    this.pdfCreateBGGrayColumn(true);
    // bottom banner
    this.pdfCreateBottomBanner();

    this.pdfCreateTitle();

    return Observable.create((observer) => {
      try {
        if (this.logo) {
          // this.doc.addImage(config.PDF.PDFLogoBackgroundWhiteImage, 'JPEG', 138, 42, 63, 18);
          // this.doc.addImage(this.logo, 'PNG', 138, 42, 63, 18);

          this.getImageDimensions(this.logo).subscribe((size: { width; height }) => {
            let x = (this.logoSize.height * size.width) / size.height;
            let y = (this.logoSize.width * size.height) / size.width;

            // over the max height
            if (x <= this.logoSize.width && y >= this.logoSize.height) {
              // this.doc.addImage(config.PDF.PDFLogoBackgroundWhiteImage, 'JPEG',
              // 	210 - (this.logoSize.width - x) / 2 - x - 10,
              // 	60 - this.logoSize.height, x, this.logoSize.height);
              this.doc.addImage(
                this.logo,
                'JPG',
                210 - (this.logoSize.width - x) / 2 - x - 10,
                60 - this.logoSize.height,
                x,
                this.logoSize.height
              );
            }
            // over the max width
            else if (y <= this.logoSize.height && x >= this.logoSize.width) {
              // this.doc.addImage(config.PDF.PDFLogoBackgroundWhiteImage, 'JPEG',
              // 	210 - this.logoSize.width - 10,
              // 	60 - y, this.logoSize.width, y);
              this.doc.addImage(this.logo, 'JPG', 210 - this.logoSize.width - 10, 60 - y, this.logoSize.width, y);
            }
            // not over the max width nor height
            else if (y <= this.logoSize.height && x <= this.logoSize.width) {
              // this.doc.addImage(config.PDF.PDFLogoBackgroundWhiteImage, 'JPEG',
              // 	210 - (this.logoSize.width - x) / 2 - x - 10,
              // 	60 - y, this.logoSize.width, y);
              this.doc.addImage(
                this.logo,
                'JPG',
                210 - (this.logoSize.width - x) / 2 - x - 10,
                60 - y,
                this.logoSize.width,
                y
              );
            }

            observer.next({});
            observer.complete();
          });
        } else {
          observer.next({});
          observer.complete();
        }
      } catch (error) {
        observer.error(error);
      }
    });
  }

  private getImageDimensions(file): Observable<any> {
    return Observable.create((observer) => {
      try {
        let i = new Image();
        i.onload = function () {
          observer.next({ width: i.width, height: i.height });
          observer.complete();
        };
        i.src = file;
      } catch (error) {
        observer.error(error);
      }
    });
  }

  private pdfCreateImages(imgUrl: string, index: number, title = '', item?: HubEvent | HubPlace) {
    let afterAdded = () => {
      if (this.progressCallback) {
        let percentage = Math.round((this.pdfImageBase64.length / (this.itemCount + 1)) * 100);
        percentage = Math.min(100, percentage);
        this.progressCallback(percentage);
      }
      if (!this.printPDFIfLoaded()) {
        let itemImageCount = this.pdfImageBase64.filter(
          (x) => !x.isTripTitle && x.title !== 'topBG' && x.title !== 'logo' && x.title !== 'logoBG'
        ).length;
        const log = `images count: ${itemImageCount}, index: ${title}, this.itemCount: ${this.itemCount}, errorCount: ${
          this.errorName.length
        }, if: ${itemImageCount >= this.itemCount - this.errorName.length}`;
        console.log(log);
      }
    };

    // images from Amazon S3 has CORS problems (have already set in the Amazon dashboard, but still not work), so here is to get the image base64 from server side
    if (imgUrl && imgUrl.indexOf('s3.amazonaws.com') >= 0) {
      return this.apiService.getImageBase64(imgUrl).subscribe((data) => {
        console.log(data);
        let base64 = null;
        if (data && data.succeed) {
          base64 = data.base64;
        }
        this.pdfImageBase64.push(new ImgDataObject(false, title, index, 0, 0, base64, 0, item));
        afterAdded();
      });
    }

    if (!imgUrl.startsWith('data:')) {
      let img = document.createElement('img');
      img.crossOrigin = 'anonymous';
      let isLoaded = false;
      this.errorName = [];
      let timeout = setTimeout(() => {
        if (!isLoaded) {
          this.addToErrorList(title, imgUrl, 'timeout');
          img.removeAttribute('src');
          this.printPDFIfLoaded();
        }
      }, this.loadImageTimeout);
      img.onerror = (e) => {
        this.addToErrorList(title, imgUrl, 'error');
        isLoaded = true;
        clearTimeout(timeout);
        this.printPDFIfLoaded();
      };
      img.onload = (x) => {
        let base64 = null;
        let ratio = 0,
          width = 0,
          height = 0;
        isLoaded = true;
        clearTimeout(timeout);
        try {
          let image = x.srcElement as HTMLImageElement;
          ratio = image.width / image.height;
          width = image.width;
          height = image.height;
          base64 = this.getBase64Image(image);
        } catch (e) {
          base64 = null;
          const log = `error when get base64 from image`;
          console.log(log);
        } finally {
          if (base64) {
            this.pdfImageBase64.push(new ImgDataObject(false, title, index, width, height, base64, ratio, item));
          }
        }

        afterAdded();
      };
      img.src = imgUrl;
    } else {
      this.pdfImageBase64.push(new ImgDataObject(false, title, index, 0, 0, imgUrl, 3.3, item));
      afterAdded();
    }
  }

  private getBase64Image(img: HTMLImageElement, imageType = 'image/jpeg') {
    let canvas = document.createElement('canvas');
    canvas.width = img.width;
    canvas.height = img.height;
    let ctx = canvas.getContext('2d');
    ctx.drawImage(img, 0, 0);
    let dataURL = canvas.toDataURL(imageType);
    return dataURL;
  }

  private printPDFIfLoaded() {
    if (this.isAllLoaded) {
      return false;
    }

    let itemImageCount = this.pdfImageBase64.filter(
      (x) => !x.isTripTitle && x.title !== 'topBG' && x.title !== 'logo' && x.title !== 'logoBG'
    ).length;
    if (itemImageCount >= this.itemCount - this.errorName.length) {
      this.isAllLoaded = true;
      const log1 = `images count: ${itemImageCount}, this.itemCount: ${this.itemCount}, errorCount: ${this.errorName.length}, if: allLoaded`;
      const log2 = 'errorIndex: ' + this.errorName.join(', ');
      console.log(log1);
      console.log(log2);
      if (!this.isPrintVersion) {
        this.saveOnlinePDF();
      } else {
        this.savePrintPDF();
      }
      return true;
    }
    return false;
  }

  private addToErrorList(title, imageUrl, errorOrTimeout = 'error') {
    !this.errorName.filter((x) => x === title).length && this.errorName.push(title);
    const log = `loading image ${errorOrTimeout}, index: ${title}, errorcount: ${this.errorName.length}, imgUrl: ${imageUrl}`;
    console.log(log);
  }

  private pdfCreateEventTitle(item: HubEvent | HubPlace, index: number, y) {
    //let y = (52 * index) + 23;
    this.doc.setFontSize(16);
    this.doc.setTextColor(0, 0, 0);
    item.title = item.title || item.name;
    item.title = this.replaceSpecialChar(item.title);
    let lines = this.doc.splitTextToSize(item.title, 140);
    this.doc.text(64, y, this.getAllowedText(lines[0]));
    this.twoLinesTitle = false;
    if (lines.length >= 2) {
      this.twoLinesTitle = true;
      y += 7;
      this.doc.text(64, y, this.getAllowedText(lines[1]));
    }
    this.yPostion = y;
  }

  public venueName(event): string {
    if (!event || !event.startTime) return '';
    return event.venue || (event.venueInfo ? event.venueInfo.name : event.owner ? event.owner.name : '');
  }

  private pdfCreateEventLocation(item: HubEvent | HubPlace, index: number, y) {
    //let y = (52 * index) + 30;
    y = !this.isPrintVersion && this.twoLinesTitle ? y + 8 : y;
    this.doc.addImage(config.PDF.PDFMapMarkerImage, 'JPEG', 64.9, y - 4, 3.5, 5);

    this.doc.setFontSize(10);
    this.doc.setTextColor(0, 0, 0);
    let address = '';
    if (typeof item.address === 'string') {
      address = item.address;
    } else {
      if (item.address && item.address.address) {
        address = item.address.address;
      }
    }
    const venue = this.venueName(item);
    address = (venue ? venue + ', ' : '') + address || '(no location)';
    if (!this.isPrintVersion) {
      address = address.length > 75 ? `${address.substr(0, 75)} ...` : address;
      this.doc.text(71, y, this.getAllowedText(address));
    } else {
      let lines = this.doc.splitTextToSize(address, 130);
      for (let line of lines) {
        this.doc.text(71, y, this.getAllowedText(line));
        y += 4;
      }
    }
    this.yPostion = y;
  }

  private pdfCreateEventTime(item: HubEvent | HubPlace, index: number, y) {
    //let y = (52 * index) + 37;
    y = !this.isPrintVersion && this.twoLinesTitle ? y + 8 : y;
    this.doc.addImage(config.PDF.PDFTimerImage, 'JPEG', 63.8, y - 4, 6, 6);

    if (item.eventTimes && item.eventTimes.length > 1) {
      this.doc.setFontSize(10);
      this.doc.setTextColor(0, 0, 0);
      let text = '';
      if (!this.isPrintVersion) {
        text = `${item.eventTimes.length} dates . `;
      } else {
        text = `${Math.min(item.eventTimes.length, this.maxTimeBoxCount)} dates . `;
      }
      text += `${EventTimeService.toPDFTimeFormat(
        new Date(item.eventTimes[0].startTime)
      )} - ${EventTimeService.toPDFTimeFormat(new Date(item.eventTimes[item.eventTimes.length - 1].startTime))}`;
      this.doc.text(71, y, text);
    }
    this.yPostion = y;
  }

  private pdfCreateEventTimeBox(item: HubEvent | HubPlace, index: number, y) {
    let x = 71;

    y = !this.isPrintVersion && this.twoLinesTitle ? y + 8 : y;
    //let y = (52 * index) + 37;

    if (item.eventTimes && item.eventTimes.length > 1) {
      y += 6;
      item.eventTimes.forEach((time, i) => {
        if (i < this.maxTimeBoxCount) {
          if (!this.isPrintVersion) {
            if (i <= 2) {
              //this.pdfTimeBox(time.startTime, item.timezone, (36 * i) + x, y);
              this.pdfTimeBox(time.startTimeFormatted, 36 * i + x, y, time.startTime);
            }
          } else {
            if (i && i % 3 === 0) {
              y += 12.5;
              if (y > 270) {
                this.pdfNewPage();
                y = 15;
              }
            }
            if (i < this.maxTimeBoxCount) {
              i = i % 3;
              //this.pdfTimeBox(time.startTime, item.timezone, (36 * i) + x, y);
              this.pdfTimeBox(time.startTimeFormatted, 36 * i + x, y, time.startTime);
            }
          }
        }
      });
      if (item.eventTimes.length > 3 && !this.isPrintVersion) {
        this.doc.setDrawColor(197, 197, 197);
        this.doc.setFillColor(255, 255, 255);
        this.doc.roundedRect(x + 108, y - 4, 10, 10, 1, 1, 'FD');
        this.doc.setFontSize(10);
        this.doc.setTextColor(72, 118, 255);
        this.doc.text(x + 111, y + 2, `+${item.eventTimes.length - 3}`);
      }
    } else {
      //this.pdfTimeBox(item.startTime, item.timezone, x, y);
      this.pdfTimeBox(item.startTimeFormatted, x, y, item.startTime);
    }
    this.yPostion = y;
  }

  private pdfCreateTriangleAtLeftTop(y1) {
    let x1 = 12;
    //let y1 = (52 * index) + 35;
    let x2 = x1 + 6;
    let y2 = y1 - 3;
    let x3 = x1 + 6;
    let y3 = y1;

    if (this.mainColor) {
      let color = hexToRgb(this.mainColor);
      this.doc.setFillColor(color.r, color.g, color.b);
    } else {
      this.doc.setFillColor(123, 201, 48);
    }
    this.doc.triangle(x1, y1, x2, y2, x3, y3, 'F');
  }

  private pdfCreatePlaceInfoForPrint(item: HubPlace, y) {
    // y = (!this.isPrintVersion && this.twoLinesTitle) ? y + 8 : y;
    this.doc.setFontSize(10);
    this.doc.setTextColor(0, 0, 0);

    let link = item.website; //item.hostLink || item.website;
    if (link) {
      this.doc.addImage(config.PDF.PDFWebImage, 'PNG', 64.7, y - 2.8, 4, 3);
      let lines = this.doc.splitTextToSize(link, 130);
      let index = 0;
      for (let line of lines) {
        this.doc.text(71, y, this.getAllowedText(line));
        index++;
        index === lines.length ? (y += 6) : (y += 4);
      }
    }

    if (y > 280) {
      this.pdfNewPage();
      y = 15;
    }

    if (item.phone) {
      this.doc.addImage(config.PDF.PDFPhoneImage, 'PNG', 64.7, y - 2.8, 4, 3);
      this.doc.text(71, y, this.getAllowedText(item.phone));
      y += 6;
    }

    if (y > 280) {
      this.pdfNewPage();
      y = 15;
    }

    if (item.priceRange) {
      this.doc.addImage(config.PDF.PDFPriceImage, 'PNG', 64.7, y - 2.8, 4, 3);
      this.doc.text(71, y, this.getAllowedText(this.getPriceRangeSymbol(item.priceRange)));
      y += 6;
    }

    if (y > 280) {
      this.pdfNewPage();
      y = 15;
    }

    if (!isNullOrUndefined(item.checkins)) {
      this.doc.addImage(config.PDF.PDFCheckinsImage, 'PNG', 64.7, y - 2.8, 4, 3);
      this.doc.text(71, y, item.checkins.toString());
      y += 6;
    }

    this.yPostion = y;
  }

  private getPriceRangeSymbol(price: string | number): string {
    if (!price) return '';

    if (price.toString().startsWith('$')) return price.toString();

    let array: string[] = [];
    for (let i = 0; i < +price; i++) {
      array.push('$');
    }
    return array.join('');
  }

  private pdfCreateDescriptionForPrint(item: HubEvent | HubPlace, y, initIndex: number) {
    y = !this.isPrintVersion && this.twoLinesTitle ? y + 8 : y;
    if (y > 280) {
      this.pdfNewPage();
      y = 15;
    }
    let x = 71;
    this.doc.setFontSize(10);
    this.doc.setTextColor(0, 0, 0);
    let lines = 0;

    // if (item.description || item['about']) {
    if (item.description) {
      let description = item.description;
      // let about = item['about'];
      // if (!description && about) {
      //     description = about;
      // }

      description = this.replaceSpecialChar(description);
      description = this.replaceBr(description);

      let brs = description.split('<br>');
      for (let br of brs) {
        let sections = this.doc.splitTextToSize(br, 130);
        for (let section of sections) {
          if (y > 280) {
            this.pdfNewPage();
            y = 15;
          }
          this.doc.text(x, y, this.getAllowedText(section));
          y += 4.2;
          lines++;
        }
      }
    }
    if (lines <= 3) {
      y += 12.6 - lines * 4.2;
    }
    this.yPostion = y;
  }

  private pdfCreateEventDescription(item: HubEvent | HubPlace, y, initIndex: number) {
    //let y = (52 * index) + 63;
    y = this.twoLinesTitle ? y + 8 : y;
    //if (!this.isEvent) {
    if (!item.startTime) {
      y -= 8.5;
    }
    let x = 71;

    if (item.eventTimes && item.eventTimes.length > 1) {
      y += 6;
    }

    this.doc.setFontSize(10);
    this.doc.setTextColor(0, 0, 0);
    if (item.description || item['about']) {
      let description = item.description;
      let about = item['about'];
      if (!description && about) {
        description = about;
      }

      description = this.replaceSpecialChar(description);

      let moreInfoY = y;
      let lines = this.doc.splitTextToSize(description, 130);

      if (lines.length > 2) {
        if (this.twoLinesTitle) {
          if (item.eventTimes && item.eventTimes.length > 1) {
            lines = lines.slice(0, 1);
          } else {
            lines = lines.slice(0, 2);
          }
          lines = this.cutWord(lines);
        } else {
          if (lines.length > 3) {
            if (item.eventTimes && item.eventTimes.length > 1) {
              lines = lines.slice(0, 3);
            } else {
              lines = lines.slice(0, 4);
            }
            lines = this.cutWord(lines);
          }
        }
      }
      // for the first page's 4th event, the description space is limit
      if (initIndex === 3 && item.startTime) {
        if (this.twoLinesTitle) {
          if (item.eventTimes && item.eventTimes.length > 1) {
            lines = [];
          } else {
            lines = lines.slice(0, 1);
          }
        } else {
          if (item.eventTimes && item.eventTimes.length > 1) {
            lines = lines.slice(0, 1);
          } else {
            lines = lines.slice(0, 2);
          }
        }
        lines = this.cutWord(lines);
      }
      moreInfoY += lines.length * 4.2;

      let newLines = [];
      for (let line of lines) {
        newLines.push(this.getAllowedText(line));
      }

      if (newLines && newLines.length) {
        this.doc.text(x, y, newLines);
      }

      // events
      if (item.startTime && item.eventLink) {
        this.doc.setTextColor(0, 0, 255);
        this.doc.textWithLink('Click here for more information', x, moreInfoY, { url: item.eventLink });
      }
      // places
      if (!item.startTime) {
        const link =
          item.hostLink || item['website'] || `${config.serverDomain}/${item.hubName}/place-details/${item._id}`;
        this.doc.setTextColor(0, 0, 255);
        this.doc.textWithLink('Click here for more information', x, moreInfoY, { url: link });
      }
    } else {
      // events
      if (item.startTime && item.eventLink) {
        this.doc.setTextColor(0, 0, 255);
        this.doc.textWithLink('Click here for more information', x, y, { url: item.eventLink });
      }
      // places
      if (!item.startTime) {
        const link =
          item.hostLink || item['website'] || `${config.serverDomain}/${item.hubName}/place-details/${item._id}`;
        this.doc.setTextColor(0, 0, 255);
        this.doc.textWithLink('Click here for more information', x, y, { url: link });
      }
    }
  }

  private pdfNewPage() {
    this.doc.addPage();
    this.pdfCreateBGGrayColumn();
    this.pdfCreateBottomBanner();
    this.doc.setFontSize(10);
    this.doc.setTextColor(0, 0, 0);
    this.yPostion = 15;
  }

  private cutWord(lines) {
    for (let i = lines.length - 1; i >= 0; i--) {
      if (lines[i].indexOf('.') !== -1) {
        lines[i] = lines[i].substr(0, lines[i].indexOf('.') + 1);
        break;
      } else if (lines[i].indexOf('!') !== -1) {
        lines[i] = lines[i].substr(0, lines[i].indexOf('!') + 1);
        break;
      } else {
        lines = lines.slice(0, i);
      }
    }
    return lines;
  }

  private pdfCenteredText(text, y) {
    let textWidth =
      (this.doc.getStringUnitWidth(text) * this.doc.internal.getFontSize()) / this.doc.internal.scaleFactor;
    let pageWidth = this.doc.internal.pageSize.width ? this.doc.internal.pageSize.width : 210;
    let textOffset = (pageWidth - textWidth) / 2;
    this.doc.text(textOffset, y, text);
  }

  //private pdfTimeBox(time: string, timezone: string, startTimeFormatted: string, x: number, y: number) {
  private pdfTimeBox(startTimeFormatted: string, x: number, y: number, startTime?: any) {
    let startTimeFormattedSeg, date, month, day, eventTime;
    if (startTimeFormatted.indexOf(' @ ') !== -1) {
      startTimeFormattedSeg = startTimeFormatted.split(' @ ');
      date = startTimeFormattedSeg[0].split(' ');
      month = date[0];
      day = date[1];
      eventTime = startTimeFormattedSeg[1];
    } else {
      startTimeFormattedSeg = startTimeFormatted.split(' ');
      month = startTimeFormattedSeg[0];
      day = startTimeFormattedSeg[1];
      eventTime = 'All Day';
    }

    const thisYear = new Date(startTime).getFullYear();
    // let dayOfWeek = new Date(`${month} ${day} ${thisYear}`).getDay();
    // let dayOfWeekText = '';
    // if (dayOfWeek === NaN && startTime) {
    //     dayOfWeek = new Date(startTime).getDay();
    // }

    let startDate = new Date(`${thisYear}/${monthText[month].num}/${day} 10:00:00`);
    // if (startTime.indexOf('T') >= 0) {
    //     startDate = startTime.split('T')[0];
    // }
    const dayOfWeek = new Date(startDate || startTime).getDay();
    const dayOfWeekText = dayOfWeekDefination[dayOfWeek].short;

    //let timeBox = EventTimeService.toPDFTimeBoxFormat(new Date(time));

    this.doc.setDrawColor(197, 197, 197);
    this.doc.setFillColor(255, 255, 255);
    this.doc.roundedRect(x, y - 4, 34, 10, 1, 1, 'FD');

    this.doc.setFontSize(8);
    this.doc.setTextColor(255, 0, 0);
    this.doc.text(x + 2, y, month.toUpperCase());

    this.doc.setFontSize(8);
    this.doc.setTextColor(0, 0, 0);
    //let timeText = `${timeBox.day} ${timeBox.time} ${timezone || ''}`;
    let timeText = `${dayOfWeekText} ${eventTime}`;
    this.doc.text(timeText.length > 15 && timeText.indexOf('Wed') >= 0 ? x + 8.2 : x + 9, y + 2, timeText);

    this.doc.setFontSize(12);
    this.doc.text(x + 2, y + 4, day);
  }

  private pdfCreateBGGrayColumn(isFirstPage = false) {
    // the gray column background behind images
    this.doc.setDrawColor(0);
    this.doc.setFillColor(235, 243, 241);
    if (isFirstPage) {
      this.doc.rect(18, 84, 40, 300, 'F');
    } else {
      this.doc.rect(18, 0, 40, 300, 'F');
    }
  }

  private pdfCreateTitle() {
    this.doc.setFontSize(22);
    this.doc.setTextColor(0, 0, 0);
    let title = '';
    if (this.type === PDFType.event) {
      title = "EVENTS YOU CAN'T MISS";
    } else if (this.type === PDFType.place) {
      title = 'PLACES TO DISCOVER';
    } else if (this.type === PDFType.tour) {
      title = 'ITINERARIES';
    } else if (this.type === PDFType.trip) {
      title = 'TRIPS';
    }
    if (this.pdfDateRange) {
      this.doc.text(12, 70, title);
      this.pdfCreateEventPdfDateRange();
    } else {
      this.doc.text(12, 75, title);
    }
  }

  private pdfCreateEventPdfDateRange() {
    this.doc.setFontSize(13);
    this.doc.setTextColor(23, 167, 13);
    this.doc.text(12, 77, this.pdfDateRange.toUpperCase());
  }

  private pdfCreateBottomBanner() {
    if (this.mainColor) {
      let color = hexToRgb(this.mainColor);
      this.doc.setFillColor(color.r, color.g, color.b);
    } else {
      this.doc.setFillColor(134, 215, 45);
    }

    this.doc.rect(0, 284, 210, 14, 'F');

    this.doc.setFontSize(12);
    this.doc.setTextColor(255, 255, 255);

    let at = 'EVENTS';
    let location = '';

    if (this.type === PDFType.place) {
      at = 'PLACES';
      if (!this.placeCalendarURL) {
        location = window.location.href;
      } else {
        location = this.placeCalendarURL.startsWith('http')
          ? this.placeCalendarURL
          : `https://${this.placeCalendarURL}`;
      }
    } else if (this.type === PDFType.event) {
      if (!this.eventCalendarURL) {
        location = window.location.href;
      } else {
        location = this.eventCalendarURL.startsWith('http')
          ? this.eventCalendarURL
          : `https://${this.eventCalendarURL}`;
      }
      if (this.pdfDateFilterRange) {
        location += this.pdfDateFilterRange;
      }
    } else {
      at = 'ITINERARIES';
      if (!this.moreToursURL) {
        location = window.location.href;
        if (location.indexOf('/pdf/') >= 0) {
          // from print pdf
          location = location.split('/pdf/')[0] + '/trips';
        }
      } else {
        location = this.moreToursURL.startsWith('http') ? this.moreToursURL : `https://${this.moreToursURL}`;
      }
    }

    this.pdfCenteredText(`MORE ${at}: ${location}`, 292);
  }

  private pdfCreatePlaceCategory(item: HubPlace, y) {
    y = !this.isPrintVersion && this.twoLinesTitle ? y + 8 : y;
    this.doc.addImage(config.PDF.PDFCategoryImage, 'PNG', 64.7, y - 2.8, 4, 3);
    this.doc.setFontSize(10);
    this.doc.setTextColor(0, 0, 0);
    let newCategoryList = item.categoryList.map((y) => {
      let temp = y
        .replace(/(_)+/g, ' ')
        .split(' ')
        .map((z) => (z === 'of' ? z : upperFirstLetter(z)));
      return temp.join(' ');
    });
    let category = newCategoryList.join(', ');
    if (!this.isPrintVersion) {
      // let category = item.category.length > 70 ? `${item.category.substr(0, 70)} ...` : item.category;
      category = category.length > 70 ? `${category.substr(0, 70)} ...` : category;
      this.doc.text(71, y, this.getAllowedText(category));
    } else {
      let lines = this.doc.splitTextToSize(category, 130);
      for (let line of lines) {
        this.doc.text(71, y, this.getAllowedText(line));
        y += 4;
      }
    }
    this.yPostion = y;
  }

  private replaceSpecialChar(description: string) {
    if (!this.isPrintVersion) {
      description = description.replace(/(\n)+|(\r\n)+/g, '');
      description = description.replace(/(<br>)+/g, '');
      description = description.replace(/(<br\/>)+/g, '');
      description = description.replace(/(<p>)+/g, '');
      description = description.replace(/(<\/p>)+/g, '');
    } else {
      description = description.replace(/(\n)+|(\r\n)+/g, '<br><br>');
      description = description.replace(/(<br\/>)+/g, '<br>');
      description = description.replace(/(<p>)+/g, '<br><br>');
      description = description.replace(/(<\/p>)+/g, '<br><br>');
    }

    description = description.replace(/(<i>)+/g, '');
    description = description.replace(/(<\/i>)+/g, '');
    description = description.replace(/(<b>)+/g, '');
    description = description.replace(/(<\/b>)+/g, '');
    description = description.replace(/(<strong>)+/g, '');
    description = description.replace(/(<\/strong>)+/g, '');
    description = description.replace(/(<a.*?>)+/g, '');
    description = description.replace(/(<\/a>)+/g, '');
    description = description.replace(/(&#39;)+/g, "'");
    description = description.replace(/(&quot;)+/g, '"');
    description = description.replace(/(&gt;)+/g, '>');
    description = description.replace(/(&lt;)+/g, '<');

    description = this.getAllowedText(description);

    // description = description.replace(/(��)+/g, "'");
    // description = description.replace(/(��)+/g, "'");
    // description = description.replace(/(��)+/g, "'");
    // description = description.replace(/(��)+/g, "\"");
    // description = description.replace(/(��)+/g, "\"");
    // description = description.replace(/(?)+/g, "");
    // description = description.replace(/(�C)+/g, "-");
    // description = description.replace(/(��)+/g, "-");
    // description = description.replace(/(��)+/g, "...");
    // description = description.replace(/(?)+/g, "");
    // description = description.replace(/(?)+/g, "");
    // description = description.replace(/(?)+/g, "");
    // description = description.replace(/\s+/g, " ");
    // description = description.replace(/(\. ?)+/g, ". ");
    // description = description.replace(/(\! ?)+/g, ". ");

    if (description.startsWith(' ')) {
      description = description.substr(1, description.length - 1);
    }

    return description;
  }

  private getAllowedText(section): string {
    if (!this.isAllSymbolAllowed(section)) {
      const log = `${section}: contains invalid symbols`;
      console.log(log);
      let symbols = section.toString().split('');
      let newText = '';
      for (let symbol of symbols) {
        if (this.isAllSymbolAllowed(symbol)) {
          newText += symbol;
        }
      }
      const newT = `newText: ${newText}`;
      console.log(newT);
      section = newText;
    }
    return section;
  }

  private isAllSymbolAllowed(str) {
    if (!str) {
      return true;
    }
    return new RegExp('^[-+A-Za-z_0-9~`!@#$%^&*()\'".:,/\\\\;<>{}\\[\\]|=? ]+$').test(str);
  }

  private replaceBr(description) {
    while (description.startsWith('<br>') || description.endsWith('<br>')) {
      description = this.removeBr(description);
    }
    return description;
  }

  private removeBr(description) {
    if (description.startsWith('<br>')) {
      description = description.substr(4, description.length - 4);
    }
    if (description.endsWith('<br>')) {
      description = description.substr(0, description.length - 4);
    }
    return description;
  }
}

export class ImgDataObject {
  constructor(
    public isTripTitle: boolean,
    public title: string,
    public index: number,
    public width: number,
    public height: number,
    public base64: string,
    public widthHeightRatio: number,
    public item: HubEvent | HubPlace
  ) {}
}

export enum PDFType {
  event,
  place,
  tour,
  trip,
}

export const dayOfWeekDefination = [
  { name: 'Sunday', short: 'Sun' },
  { name: 'Monday', short: 'Mon' },
  { name: 'Tuesday', short: 'Tue' },
  { name: 'Wednesday', short: 'Wed' },
  { name: 'Thursday', short: 'Thu' },
  { name: 'Friday', short: 'Fri' },
  { name: 'Saturday', short: 'Sat' },
];

export const monthText = {
  Jan: { num: '01' },
  Feb: { num: '02' },
  Mar: { num: '03' },
  Apr: { num: '04' },
  May: { num: '05' },
  Jun: { num: '06' },
  Jul: { num: '07' },
  Aug: { num: '08' },
  Sep: { num: '09' },
  Oct: { num: '10' },
  Nov: { num: '11' },
  Dec: { num: '12' },
};



// WEBPACK FOOTER //
// D:/Work/Development/vsts_build/_work/2/s/web/src/services/export.csv.service.ts